/**
 * NotificationBell - Notification icon with unread count badge and dropdown.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { useMyNotifications, useUnreadCount, markNotificationRead, markAllNotificationsRead } from '@/shared/hooks/useNotifications';
import { useAuthStore } from '@/shared/stores/authStore';
import type { AuthorNotificationRow } from '@/core/supabase/database.types';

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  return `${Math.floor(hours / 24)}天前`;
}

function typeIcon(type: string) {
  const map: Record<string, string> = { review_approved: '✅', review_rejected: '❌', revision_requested: '⚠️', novel_delisted: '🔒', novel_relisted: '🔓', account_warning: '🚨', system_notice: '📢' };
  return map[type] ?? '🔔';
}

function NotificationItem({ item, onRead }: { item: AuthorNotificationRow; onRead: (id: string) => void }) {
  return (
    <button type="button" onClick={() => onRead(item.id)} className={`w-full text-left px-4 py-3 hover:bg-accent/50 transition-colors ${!item.is_read ? 'bg-primary/5' : ''}`}>
      <div className="flex items-start gap-2">
        <span className="text-base shrink-0" aria-hidden="true">{typeIcon(item.type)}</span>
        <div className="min-w-0 flex-1">
          <p className={`text-sm ${!item.is_read ? 'font-medium' : ''}`}>{item.title}</p>
          {item.content && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{item.content}</p>}
          <p className="text-xs text-muted-foreground mt-0.5">{timeAgo(item.created_at)}</p>
        </div>
        {!item.is_read && <span className="mt-1 h-2 w-2 rounded-full bg-primary shrink-0" />}
      </div>
    </button>
  );
}

export function NotificationBell() {
  const user = useAuthStore((s) => s.user);
  const { data: unread } = useUnreadCount();
  const { data: notifications, refetch } = useMyNotifications(20);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const handleRead = useCallback(async (notificationId: string) => { await markNotificationRead(notificationId); refetch(); }, [refetch]);
  const handleMarkAllRead = useCallback(async () => { if (!user?.id) return; await markAllNotificationsRead(user.id); refetch(); }, [user?.id, refetch]);

  if (!user) return null;
  const count = typeof unread === 'number' ? unread : 0;

  return (
    <div ref={dropdownRef} className="relative">
      <button type="button" onClick={() => setOpen(!open)} className="relative rounded-lg p-2 hover:bg-accent transition-colors" aria-label={`通知${count > 0 ? ` (${count}条未读)` : ''}`}>
        <svg aria-hidden="true" className="h-5 w-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {count > 0 && <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">{count > 99 ? '99+' : count}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-y-auto rounded-xl border border-border bg-card shadow-lg z-50">
          <div className="sticky top-0 flex items-center justify-between bg-card border-b border-border px-4 py-2">
            <h3 className="text-sm font-semibold">通知</h3>
            {count > 0 && <button type="button" onClick={handleMarkAllRead} className="text-xs text-primary hover:underline">全部已读</button>}
          </div>
          {!notifications || notifications.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">暂无通知</div>
          ) : (
            <div className="divide-y divide-border">{notifications.map((n) => <NotificationItem key={n.id} item={n} onRead={handleRead} />)}</div>
          )}
        </div>
      )}
    </div>
  );
}